// src/components/layout/Footer.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { FiInstagram, FiTwitter, FiFacebook, FiLinkedin, FiMail, FiPhone } from 'react-icons/fi';

const Footer = () => (
    <footer className="bg-surface-900 dark:bg-surface-950 text-white transition-colors duration-300">
        {/* Main grid */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-14 pb-10">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-10">

                {/* Brand col */}
                <div className="sm:col-span-2 md:col-span-1">
                    <div className="flex items-center gap-2 mb-4">
                        <div className="w-8 h-8 rounded-xl bg-brand-500 flex items-center justify-center">
                            <span className="text-white font-bold text-sm">H</span>
                        </div>
                        <span className="font-display text-xl font-bold text-white">Hoommy</span>
                    </div>
                    <p className="text-surface-400 text-sm leading-relaxed mb-5">
                        La plataforma #1 para encontrar arriendos en Colombia. Conectamos arrendatarios, propietarios e inmobiliarias de forma segura.
                    </p>
                    <div className="flex gap-3">
                        {[
                            { Icon: FiInstagram, label: 'Instagram' },
                            { Icon: FiFacebook, label: 'Facebook' },
                            { Icon: FiTwitter, label: 'Twitter' },
                            { Icon: FiLinkedin, label: 'LinkedIn' },
                        ].map(({ Icon, label }) => (
                            <button
                                key={label}
                                type="button"
                                aria-label={label}
                                className="w-9 h-9 rounded-lg bg-surface-800 hover:bg-brand-500 flex items-center justify-center transition-colors duration-200"
                            >
                                <Icon className="w-4 h-4" />
                            </button>
                        ))}
                    </div>
                </div>

                {/* Explorar */}
                <div>
                    <h3 className="font-display font-semibold text-white text-sm uppercase tracking-wider mb-4">Explorar</h3>
                    <ul className="space-y-2.5">
                        {[
                            { label: 'Todas las propiedades', to: '/' },
                            { label: 'Para estudiantes', to: '/estudiantes' },
                            { label: 'Casas', to: '/?tipo=casa' },
                            { label: 'Apartamentos', to: '/?tipo=apartamento' },
                            { label: 'Habitaciones', to: '/?tipo=habitacion' },
                            { label: 'Planes y precios', to: '/planes' },
                        ].map(({ label, to }) => (
                            <li key={label}>
                                <Link to={to} className="text-surface-400 hover:text-brand-400 text-sm transition-colors">{label}</Link>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Compañía */}
                <div>
                    <h3 className="font-display font-semibold text-white text-sm uppercase tracking-wider mb-4">Compañía</h3>
                    <ul className="space-y-2.5">
                        {[
                            { label: 'Nosotros', to: '/nosotros' },
                            { label: 'Blog', to: '/blog' },
                            { label: 'Contacto', to: '/contacto' },
                        ].map(({ label, to }) => (
                            <li key={label}>
                                <Link to={to} className="text-surface-400 hover:text-brand-400 text-sm transition-colors">{label}</Link>
                            </li>
                        ))}
                    </ul>

                    <h3 className="font-display font-semibold text-white text-sm uppercase tracking-wider mb-4 mt-6">Contacto</h3>
                    <ul className="space-y-2.5">
                        <li className="flex items-center gap-2 text-surface-400 text-sm">
                            <FiMail className="w-4 h-4 text-brand-400 flex-shrink-0" />
                            hola@myhoommy.com
                        </li>
                        <li className="flex items-center gap-2 text-surface-400 text-sm">
                            <FiPhone className="w-4 h-4 text-brand-400 flex-shrink-0" />
                            +57 300 000 0000
                        </li>
                    </ul>
                </div>

                {/* Legal */}
                <div>
                    <h3 className="font-display font-semibold text-white text-sm uppercase tracking-wider mb-4">Legal</h3>
                    <ul className="space-y-2.5">
                        {[
                            { label: 'Términos y Condiciones', to: '/terminos' },
                            { label: 'Política de Privacidad', to: '/privacidad' },
                        ].map(({ label, to }) => (
                            <li key={label}>
                                <Link to={to} className="text-surface-400 hover:text-brand-400 text-sm transition-colors">{label}</Link>
                            </li>
                        ))}
                    </ul>

                    {/* App badges placeholder */}
                    <div className="mt-6 space-y-2">
                        <div className="flex items-center gap-2 bg-surface-800 rounded-xl px-3 py-2.5 cursor-pointer hover:bg-surface-700 transition-colors">
                            <span className="text-xl">🍎</span>
                            <div>
                                <p className="text-[10px] text-surface-400 leading-none">Próximamente en</p>
                                <p className="text-xs font-semibold text-white">App Store</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 bg-surface-800 rounded-xl px-3 py-2.5 cursor-pointer hover:bg-surface-700 transition-colors">
                            <span className="text-xl">🤖</span>
                            <div>
                                <p className="text-[10px] text-surface-400 leading-none">Próximamente en</p>
                                <p className="text-xs font-semibold text-white">Google Play</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-surface-800">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row justify-between items-center gap-3">
                <p className="text-xs text-surface-500">
                    © {new Date().getFullYear()} Hoommy — Plataforma de Arriendos en Colombia. Todos los derechos reservados.
                </p>
                <div className="flex items-center gap-1 text-xs text-surface-500">
                    <span>Hecho con</span>
                    <span className="text-rose-500">♥</span>
                    <span>en Colombia 🇨🇴</span>
                </div>
            </div>
        </div>
    </footer>
);

export default Footer;
